import { LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import { LosseLink, type LoaderData } from '@ubo/losse-sjedel'
import type { ReactNode } from 'react'
import type { Component_Footer } from '~/graphql/types'
import Content from '../elements/Content'
import Brand from '../svg/Brand'
import LogoAlt from '../svg/LogoAlt'

export interface FooterLoaderData extends Omit<LoaderData, 'footer'> {
  footer: Component_Footer
}

export default function Footer() {
  const { footer } = useLoaderData<FooterLoaderData>()

  return (
    <footer className="relative bg-metric-cinderace overflow-hidden">
      <div className="absolute -right-20 -bottom-40 pointer-events-none">
        <Brand />
      </div>
      <ul className="container-right grid grid-cols-5 gap-x-20 md:gap-x-0 mt-20 mb-10">
        <li className="col-span-5 md:col-span-2 text-white mb-10 md:mb-0">
          <LogoAlt className="mb-6" />
          <Content className="parsecontent-md mb-6">{footer.quoteFooter?.description}</Content>
          <LosseLink
            to={footer.quoteFooter?.link?.url || ''}
            className="btn btn-metric-electrike w-1/2"
            target={footer?.quoteFooter?.link?.target || '_self'}
          >
            {footer.quoteFooter?.link?.title}
          </LosseLink>
        </li>
        {footer.menuFooter?.columns?.map((column, index) => (
          <li key={index} className="col-span-2 md:col-span-1">
            <FooterTitle>{column?.title}</FooterTitle>
            <ul>
              {column?.menu?.map((edge, subIndex) => (
                <li key={subIndex} className={`text-white py-1 ${index === 0 && subIndex === 2 ? 'mb-7' : 'mb-0'}`}>
                  <LosseLink to={edge?.link?.url || ''} className="hover:underline">
                    {edge?.link?.title}
                  </LosseLink>
                </li>
              ))}
            </ul>
          </li>
        ))}
        <li className="col-span-5 md:col-span-1 mt-10 md:mt-0">
          <FooterTitle>{footer.contactFooter?.title}</FooterTitle>
          <div className="text-white">
            <Content className="mb-12">{footer.contactFooter?.address}</Content>
            <div className="mb-12">
              <LosseLink className="flex items-center hover:underline" to={footer.contactFooter?.email?.link?.url || ''}>
                <LossePlaatjie src={footer.contactFooter?.email?.icon} maxwidth={13} />
                <span className="ml-3 mt-1">{footer.contactFooter?.email?.link?.title}</span>
              </LosseLink>
              <LosseLink className="flex items-center hover:underline" to={footer.contactFooter?.phone?.link?.url || ''}>
                <LossePlaatjie src={footer.contactFooter?.phone?.icon} maxwidth={13} />
                <span className="ml-3 mt-1">{footer.contactFooter?.phone?.link?.title}</span>
              </LosseLink>
            </div>
            <LosseLink
              className="hover:underline"
              to={footer.contactFooter?.linkedin?.url || ''}
              target={footer?.contactFooter?.linkedin?.target || '_blank'}
            >
              {footer.contactFooter?.linkedin?.title}
            </LosseLink>
          </div>
        </li>
      </ul>
      <div className="container-right xl:!pr-32 flex md:flex-row flex-col md:justify-between md:items-center gap-6 my-10">
        <svg className="w-64 h-auto -ml-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 266.46 70.25">
          <rect fill="currentColor" x="65.31" y="12.91" width="1.1" height="44.66" />
          <g>
            <path
              fill="currentColor"
              d="M71.66,40.61v-.02c0-2.1,1.54-3.97,3.85-3.97,1.29,0,2.09.37,2.86,1.02l-.56.66c-.59-.52-1.25-.88-2.33-.88-1.7,0-2.91,1.44-2.91,3.14v.02c0,1.83,1.17,3.19,3.03,3.19.88,0,1.69-.34,2.21-.77v-1.91h-2.33v-.78h3.17v3.06c-.71.64-1.79,1.17-3.08,1.17-2.41,0-3.9-1.75-3.9-3.94Z"
            />
            <path
              fill="currentColor"
              d="M80.1,41.61v-.02c0-1.63,1.15-2.95,2.72-2.95,1.68,0,2.64,1.34,2.64,2.99,0,.11,0,.18-.01.27h-4.49c.12,1.23.99,1.92,1.99,1.92.78,0,1.33-.32,1.79-.8l.53.47c-.57.64-1.26,1.06-2.33,1.06-1.56,0-2.83-1.19-2.83-2.95ZM84.6,41.28c-.09-1.03-.68-1.93-1.81-1.93-.99,0-1.73.82-1.84,1.93h3.65Z"
            />
            <path
              fill="currentColor"
              d="M86.59,41.62v-.02c0-1.6,1.25-2.96,2.9-2.96,1.07,0,1.74.45,2.27,1.01l-.56.59c-.45-.47-.95-.85-1.72-.85-1.15,0-2.03.98-2.03,2.19v.02c0,1.23.9,2.2,2.08,2.2.72,0,1.28-.36,1.73-.84l.54.5c-.57.63-1.25,1.1-2.31,1.1-1.65,0-2.9-1.35-2.9-2.94Z"
            />
            <path
              fill="currentColor"
              d="M92.68,41.61v-.02c0-1.63,1.15-2.95,2.72-2.95,1.68,0,2.64,1.34,2.64,2.99,0,.11,0,.18-.01.27h-4.49c.12,1.23.99,1.92,1.99,1.92.78,0,1.33-.32,1.79-.8l.53.47c-.57.64-1.26,1.06-2.33,1.06-1.56,0-2.83-1.19-2.83-2.95ZM97.19,41.28c-.09-1.03-.68-1.93-1.81-1.93-.99,0-1.73.82-1.84,1.93h3.65Z"
            />
            <path
              fill="currentColor"
              d="M99.53,38.76h.84v1.48c.42-.94,1.24-1.62,2.31-1.58v.91h-.07c-1.24,0-2.25.89-2.25,2.6v2.26h-.84v-5.66Z"
            />
            <path
              fill="currentColor"
              d="M104.26,42.92v-3.42h-.79v-.75h.79v-1.71h.84v1.71h1.8v.75h-1.8v3.31c0,.69.38.94.95.94.28,0,.53-.05.82-.2v.72c-.3.15-.61.24-1.02.24-.91,0-1.6-.45-1.6-1.6Z"
            />
            <path fill="currentColor" d="M108.42,36.59h.96v.93h-.96v-.93ZM108.47,38.76h.84v5.66h-.84v-5.66Z" />
            <path
              fill="currentColor"
              d="M111.6,39.51h-.78v-.73h.78v-.49c0-.65.18-1.14.49-1.46.29-.29.7-.44,1.22-.44.39,0,.66.04.93.14v.73c-.31-.09-.54-.14-.82-.14-.66,0-.99.39-.99,1.21v.46h1.8v.72h-1.79v4.92h-.84v-4.92ZM115.32,36.59h.96v.93h-.96v-.93ZM115.39,38.76h.83v5.66h-.83v-5.66Z"
            />
            <path
              fill="currentColor"
              d="M117.85,41.62v-.02c0-1.6,1.25-2.96,2.9-2.96,1.07,0,1.74.45,2.27,1.01l-.56.59c-.45-.47-.95-.85-1.72-.85-1.15,0-2.03.98-2.03,2.19v.02c0,1.23.9,2.2,2.08,2.2.72,0,1.28-.36,1.73-.84l.54.5c-.57.63-1.25,1.1-2.31,1.1-1.65,0-2.9-1.35-2.9-2.94Z"
            />
            <path
              fill="currentColor"
              d="M123.94,41.61v-.02c0-1.63,1.15-2.95,2.72-2.95,1.68,0,2.64,1.34,2.64,2.99,0,.11,0,.18-.01.27h-4.49c.12,1.23.99,1.92,1.99,1.92.78,0,1.33-.32,1.79-.8l.53.47c-.57.64-1.26,1.06-2.33,1.06-1.56,0-2.83-1.19-2.83-2.95ZM128.44,41.28c-.09-1.03-.68-1.93-1.81-1.93-.99,0-1.73.82-1.84,1.93h3.65Z"
            />
            <path
              fill="currentColor"
              d="M130.43,41.61v-.02c0-1.63,1.15-2.95,2.72-2.95,1.68,0,2.64,1.34,2.64,2.99,0,.11,0,.18-.01.27h-4.49c.12,1.23.99,1.92,1.99,1.92.78,0,1.33-.32,1.79-.8l.53.47c-.57.64-1.26,1.06-2.33,1.06-1.56,0-2.83-1.19-2.83-2.95ZM134.93,41.28c-.09-1.03-.68-1.93-1.81-1.93-.99,0-1.73.82-1.84,1.93h3.65Z"
            />
            <path
              fill="currentColor"
              d="M137.27,38.76h.84v1.48c.42-.94,1.24-1.62,2.31-1.58v.91h-.07c-1.24,0-2.25.89-2.25,2.6v2.26h-.84v-5.66Z"
            />
            <path
              fill="currentColor"
              d="M141.15,41.61v-.02c0-1.85,1.35-2.95,2.71-2.95,1.05,0,1.73.57,2.17,1.21v-3.42h.84v8h-.84v-1.14c-.46.68-1.13,1.26-2.17,1.26-1.36,0-2.71-1.07-2.71-2.94ZM146.06,41.6v-.02c0-1.3-1-2.18-2.05-2.18s-1.99.81-1.99,2.18v.02c0,1.34.92,2.19,1.99,2.19s2.05-.89,2.05-2.19Z"
            />
            <path
              fill="currentColor"
              d="M71.57,54.81v-.02c0-1.82,1.37-2.92,2.72-2.92,1.12,0,1.84.63,2.3,1.34v-3.62h.54v8h-.54v-1.25c-.48.73-1.19,1.38-2.3,1.38-1.35,0-2.72-1.08-2.72-2.9ZM76.62,54.8v-.02c0-1.44-1.12-2.4-2.28-2.4s-2.19.89-2.19,2.39v.02c0,1.47,1.02,2.41,2.19,2.41s2.28-.97,2.28-2.4Z"
            />
            <path
              fill="currentColor"
              d="M78.78,54.81v-.02c0-1.57,1.22-2.92,2.88-2.92s2.86,1.34,2.86,2.9v.02c0,1.57-1.22,2.93-2.88,2.93s-2.86-1.34-2.86-2.9ZM83.94,54.81v-.02c0-1.35-1.01-2.42-2.3-2.42s-2.28,1.08-2.28,2.4v.02c0,1.35,1.01,2.42,2.3,2.42s2.28-1.08,2.28-2.4Z"
            />
            <path
              fill="currentColor"
              d="M85.8,54.81v-.02c0-1.57,1.22-2.92,2.88-2.92s2.86,1.34,2.86,2.9v.02c0,1.57-1.22,2.93-2.88,2.93s-2.86-1.34-2.86-2.9ZM90.96,54.81v-.02c0-1.35-1.01-2.42-2.3-2.42s-2.28,1.08-2.28,2.4v.02c0,1.35,1.01,2.42,2.3,2.42s2.28-1.08,2.28-2.4Z"
            />
            <path
              fill="currentColor"
              d="M93.2,51.99h.54v1.59c.44-1,1.37-1.72,2.48-1.68v.59h-.06c-1.29,0-2.42.98-2.42,2.79v2.29h-.54v-5.59Z"
            />
            <path
              fill="currentColor"
              d="M100.99,49.91h3.17c.87,0,1.57.25,1.99.68.32.32.49.72.49,1.21v.02c0,1.04-.68,1.57-1.3,1.82.92.24,1.73.77,1.73,1.83v.02c0,1.28-1.1,2.09-2.75,2.09h-3.33v-7.67ZM106.07,51.85c0-.84-.69-1.41-1.92-1.41h-2.59v3.01h2.53c1.15,0,1.97-.56,1.97-1.58v-.02ZM104.16,53.98h-2.6v3.08h2.78c1.31,0,2.15-.6,2.15-1.57v-.02c0-.94-.82-1.49-2.33-1.49Z"
            />
            <path
              fill="currentColor"
              d="M108.79,51.99h.54v1.59c.44-1,1.37-1.72,2.48-1.68v.59h-.06c-1.29,0-2.42.98-2.42,2.79v2.29h-.54v-5.59Z"
            />
            <path
              fill="currentColor"
              d="M112.44,55.96v-.02c0-1.16,1-1.82,2.44-1.82.78,0,1.31.1,1.85.24v-.24c0-1.13-.69-1.71-1.84-1.71-.68,0-1.24.19-1.75.45l-.2-.47c.61-.28,1.22-.48,1.98-.48s1.36.21,1.76.61c.37.37.57.89.57,1.58v3.48h-.53v-.93c-.38.54-1.07,1.06-2.14,1.06s-2.16-.59-2.16-1.75ZM116.75,55.49v-.63c-.47-.12-1.1-.25-1.9-.25-1.18,0-1.84.53-1.84,1.3v.02c0,.81.77,1.28,1.62,1.28,1.14,0,2.11-.7,2.11-1.72Z"
            />
            <path
              fill="currentColor"
              d="M119.19,51.99h.54v1.04c.37-.66,1-1.17,2.02-1.17,1.41,0,2.24.96,2.24,2.3v3.42h-.54v-3.32c0-1.15-.65-1.91-1.77-1.91s-1.94.83-1.94,1.99v3.23h-.54v-5.59Z"
            />
            <path
              fill="currentColor"
              d="M125.55,54.81v-.02c0-1.82,1.37-2.92,2.72-2.92,1.12,0,1.84.63,2.3,1.34v-3.62h.54v8h-.54v-1.25c-.48.73-1.19,1.38-2.3,1.38-1.35,0-2.72-1.08-2.72-2.9ZM130.6,54.8v-.02c0-1.44-1.12-2.4-2.28-2.4s-2.19.89-2.19,2.39v.02c0,1.47,1.02,2.41,2.19,2.41s2.28-.97,2.28-2.4Z"
            />
            <path
              fill="currentColor"
              d="M136.22,53.77v-.02c0-2.17,1.62-3.97,3.83-3.97,1.37,0,2.19.5,2.98,1.23l-.41.42c-.67-.65-1.44-1.13-2.59-1.13-1.84,0-3.23,1.5-3.23,3.43v.02c0,1.94,1.41,3.45,3.24,3.45,1.11,0,1.86-.43,2.64-1.19l.38.37c-.81.8-1.69,1.34-3.05,1.34-2.19,0-3.81-1.74-3.81-3.94Z"
            />
            <path
              fill="currentColor"
              d="M144.09,54.81v-.02c0-1.57,1.22-2.92,2.88-2.92s2.86,1.34,2.86,2.9v.02c0,1.57-1.22,2.93-2.88,2.93s-2.86-1.34-2.86-2.9ZM149.25,54.81v-.02c0-1.35-1.01-2.42-2.3-2.42s-2.28,1.08-2.28,2.4v.02c0,1.35,1.01,2.42,2.3,2.42s2.28-1.08,2.28-2.4Z"
            />
            <path
              fill="currentColor"
              d="M151.49,51.99h.54v1c.36-.58.87-1.13,1.86-1.13s1.58.56,1.88,1.21c.36-.62.96-1.21,2.02-1.21,1.33,0,2.15.93,2.15,2.33v3.39h-.54v-3.32c0-1.21-.62-1.91-1.65-1.91-.94,0-1.76.72-1.76,1.97v3.25h-.54v-3.35c0-1.16-.63-1.87-1.63-1.87s-1.79.88-1.79,2v3.22h-.54v-5.59Z"
            />
            <path
              fill="currentColor"
              d="M161.87,51.99h.54v1.25c.48-.73,1.19-1.38,2.3-1.38,1.35,0,2.72,1.08,2.72,2.9v.02c0,1.82-1.37,2.93-2.72,2.93-1.12,0-1.84-.64-2.3-1.34v2.96h-.54v-7.34ZM166.85,54.81v-.02c0-1.47-1.02-2.41-2.19-2.41s-2.28.98-2.28,2.4v.02c0,1.44,1.12,2.4,2.28,2.4s2.19-.89,2.19-2.39Z"
            />
            <path fill="currentColor" d="M169.18,49.58h.54v8h-.54v-8Z" />
            <path fill="currentColor" d="M171.85,49.8h.67v.73h-.67v-.73ZM171.91,51.99h.54v5.59h-.54v-5.59Z" />
            <path
              fill="currentColor"
              d="M174.11,55.96v-.02c0-1.16,1-1.82,2.44-1.82.78,0,1.31.1,1.85.24v-.24c0-1.13-.69-1.71-1.84-1.71-.68,0-1.24.19-1.75.45l-.2-.47c.61-.28,1.22-.48,1.98-.48s1.36.21,1.76.61c.37.37.57.89.57,1.58v3.48h-.53v-.93c-.38.54-1.07,1.06-2.14,1.06s-2.16-.59-2.16-1.75ZM178.41,55.49v-.63c-.47-.12-1.1-.25-1.9-.25-1.18,0-1.84.53-1.84,1.3v.02c0,.81.77,1.28,1.62,1.28,1.14,0,2.11-.7,2.11-1.72Z"
            />
            <path
              fill="currentColor"
              d="M180.86,51.99h.54v1.04c.37-.66,1-1.17,2.02-1.17,1.41,0,2.24.96,2.24,2.3v3.42h-.54v-3.32c0-1.15-.65-1.91-1.77-1.91s-1.94.83-1.94,1.99v3.23h-.54v-5.59Z"
            />
            <path
              fill="currentColor"
              d="M187.2,54.81v-.02c0-1.57,1.25-2.92,2.85-2.92,1.05,0,1.71.48,2.23,1.03l-.38.39c-.48-.49-1.03-.92-1.86-.92-1.27,0-2.26,1.06-2.26,2.4v.02c0,1.35,1.02,2.42,2.3,2.42.79,0,1.4-.41,1.87-.93l.37.33c-.57.65-1.25,1.11-2.28,1.11-1.61,0-2.85-1.34-2.85-2.9Z"
            />
            <path
              fill="currentColor"
              d="M193.29,54.8v-.02c0-1.62,1.14-2.91,2.65-2.91s2.56,1.27,2.56,2.92c0,.1,0,.13-.01.22h-4.63c.1,1.39,1.1,2.2,2.19,2.2.87,0,1.46-.38,1.92-.88l.37.33c-.57.61-1.24,1.05-2.31,1.05-1.47,0-2.74-1.17-2.74-2.92ZM197.93,54.52c-.08-1.13-.72-2.17-2-2.17-1.11,0-1.96.93-2.06,2.17h4.06Z"
            />
          </g>
          <g>
            <path
              fill="currentColor"
              d="M45.19,35.24v.08c0,4.68,3.23,8.7,7.91,8.7h5.65s0-17.34,0-17.34h-5.69c-5.02-.06-7.87,3.88-7.87,8.56Z"
            />
            <path
              fill="currentColor"
              d="M52.91,49.42c-8.92,0-13.99-6.2-13.99-14.03v-.08c0-7.76,4.75-14.07,14.23-14.07h5.61v-8.24H12.97v8.43h12.94c3.18,0,5.68.87,7.27,2.46,1.27,1.27,1.91,2.82,1.91,4.73v.08c0,3.14-1.67,4.89-3.65,6,3.22,1.23,5.2,3.1,5.2,6.83v.08c0,5.08-4.13,7.63-10.41,7.63h-13.25v8.43h45.78v-8.24h-5.84Z"
            />
            <path fill="currentColor" d="M18.96,43.88h7.27c2.7,0,4.33-.95,4.33-3.02v-.08c0-1.87-1.39-3.02-4.53-3.02h-7.07v6.12Z" />
            <path fill="currentColor" d="M29.01,29.73v-.08c0-1.83-1.43-2.86-4.01-2.86h-6.04v5.88h5.64c2.7,0,4.41-.87,4.41-2.94Z" />
          </g>
          <g>
            <g>
              <path
                fill="currentColor"
                d="M234.7,28.1c-.32-.36-.62-.63-.82-.95-.11-.18-.11-.46-.09-.68.34-3.62-.81-6.68-3.42-9.19-1.76-1.69-3.89-2.66-6.31-2.96-4.76-.58-9.28,1.91-11.38,6.23-.09.18-.18.36-.27.55h-1.3c1.37-3.43,3.66-5.91,7.05-7.17,4.98-1.86,9.5-.92,13.33,2.77,3.49,3.37,4.1,8.14,3.19,11.4Z"
              />
              <path
                fill="currentColor"
                d="M226.42,27.79v.47c0,2.72,0,5.45,0,8.17,0,.17,0,.35-.03.52-.06.35-.3.53-.65.54-.33.01-.61-.25-.65-.59-.01-.11,0-.21,0-.32,0-3.43,0-6.86,0-10.3,0-.35.02-.68.39-.84.38-.16.65.03.92.3,3.49,3.5,6.99,6.99,10.48,10.48.09.09.19.19.26.3.18.28.15.57-.08.81-.24.25-.54.27-.84.09-.12-.07-.22-.19-.33-.29-3.01-3.01-6.03-6.02-9.04-9.03-.11-.11-.2-.25-.3-.37l-.14.07Z"
              />
              <path
                fill="currentColor"
                d="M215.42,21.54c-.4-.07-.79-.14-1.18-.21,1.38-3.32,5.42-6.01,9.85-5.41,5.44.74,8.64,5.53,8.4,10-.55-.64-1.19-1.04-1.37-1.96-.71-3.49-2.84-5.76-6.29-6.64-3.62-.93-7.28.69-9.21,3.91-.06.1-.12.2-.19.31Z"
              />
              <path
                fill="currentColor"
                d="M215.27,28.88c1.32,2.52,3.31,4.09,6.08,4.62.46.09.93.1,1.39.14.44.04.69.28.69.67,0,.39-.28.63-.71.64-3.75.11-7.71-2.65-8.94-6.22-.18-.52,0-.81.61-.98,1.31-.37,1.88-1.81,1.17-2.93-.4-.63-.99-.92-1.72-.93-.87,0-1.74,0-2.6,0-.6,0-.9-.23-.9-.67,0-.43.31-.65.91-.65.85,0,1.71,0,2.56,0,1.6.01,2.86.95,3.25,2.41.4,1.48-.21,2.94-1.59,3.76-.06.03-.11.07-.21.13Z"
              />
              <path
                fill="currentColor"
                d="M211.75,26.69c.12,1.29.45,2.46,1.02,3.54,2.04,3.91,5.3,5.95,9.68,6.21.09,0,.19,0,.28,0,.44.03.7.27.7.66,0,.4-.28.65-.75.65-2,0-3.9-.44-5.66-1.38-3.98-2.14-6.21-5.47-6.67-9.97-.08-.81.14-1.03.95-1.04.75,0,1.5,0,2.24,0,.54,0,.83.24.83.65,0,.41-.3.66-.83.66-.59,0-1.17,0-1.81,0Z"
              />
              <path
                fill="currentColor"
                d="M241.09,36.88c-.18.22-.28.48-.47.55-.19.08-.46.02-.68-.04-.11-.03-.2-.17-.29-.26-3.48-3.48-6.96-6.95-10.43-10.43-.09-.09-.2-.18-.27-.29-.18-.29-.17-.59.08-.84.24-.24.52-.25.81-.09.11.06.21.17.3.26,3.5,3.49,6.99,6.99,10.49,10.49.17.17.28.39.46.65Z"
              />
              <path
                fill="currentColor"
                d="M217.63,22.92c-.27-.31-.54-.61-.82-.94.74-2.13,3.97-3.79,6.98-3.51,2.71.25,5.5,2.74,5.86,5.23-.36.11-.72.22-1.11.34-.78-2.47-2.38-4.06-5.03-4.35-2.66-.29-4.55.95-5.87,3.23Z"
              />
              <path
                fill="currentColor"
                d="M222.33,31.2c0-.84,0-1.62,0-2.39,0-.99,0-1.98,0-2.96,0-.31.13-.59.43-.64.22-.04.5.04.69.15.12.07.2.32.2.49.01,2.02.01,4.03,0,6.05,0,.49-.26.73-.76.72-2.17-.05-3.95-.92-5.34-2.57-.28-.33-.25-.76.03-1,.31-.27.7-.22,1.02.15.86,1.01,1.93,1.67,3.23,1.95.14.03.28.04.49.06Z"
              />
              <path
                fill="currentColor"
                d="M229.21,34.07c0,.41,0,.82,0,1.22,0,.45.01.91-.01,1.36-.02.39-.29.62-.65.62-.36,0-.65-.24-.65-.62-.01-1.44-.02-2.88.01-4.33,0-.19.2-.48.37-.55.19-.08.51-.03.69.09.27.16.47.43.69.66,1.13,1.13,2.27,2.26,3.4,3.4.08.08.18.17.25.26.25.31.24.69-.03.94-.25.23-.64.24-.91-.01-.45-.42-.88-.86-1.31-1.3-.59-.59-1.18-1.19-1.77-1.78-.03.01-.07.03-.1.04Z"
              />
            </g>
            <g>
              <path
                fill="currentColor"
                d="M225.08,47.77v-7.93h.72l.48,4.78c.1,1.01.17,1.95.2,2.82h.02c.07-1.24.14-2.18.21-2.82l.51-4.78h.72v7.93h-.44v-4.92c0-.45.01-1.35.04-2.68h-.04l-.75,7.6h-.55l-.45-4.42c-.08-.76-.18-1.82-.29-3.19,0,.4,0,.92.02,1.55.01.67.02,1.05.02,1.13v4.92h-.44Z"
              />
              <path
                fill="currentColor"
                d="M230.3,44.28v-.33h.88v3.63l-.05.03c-.23.14-.53.21-.9.21-.64,0-.97-.29-.97-.86v-6.31c0-.57.32-.86.97-.86s.96.29.96.86v1.29h-.44v-1.23c0-.18-.05-.32-.15-.42s-.22-.16-.37-.16c-.15,0-.28.05-.38.16s-.15.25-.15.42v6.28c0,.33.18.49.53.49.2,0,.37-.04.52-.13v-3.08h-.44Z"
              />
              <path
                fill="currentColor"
                d="M232.48,47.77v-7.93h.72l.48,4.78c.1,1.01.17,1.95.2,2.82h.02c.07-1.24.14-2.18.21-2.82l.51-4.78h.72v7.93h-.44v-4.92c0-.45.01-1.35.04-2.68h-.04l-.75,7.6h-.55l-.45-4.42c-.08-.76-.18-1.82-.29-3.19,0,.4,0,.92.02,1.55.01.67.02,1.05.02,1.13v4.92h-.44Z"
              />
              <path fill="currentColor" d="M237,47.77v-7.6h-.78v-.33h2v.33h-.78v7.6h-.44Z" />
              <path fill="currentColor" d="M238.62,47.77v-.57h.48v.57h-.48Z" />
              <path
                fill="currentColor"
                d="M242.23,45.56v1.44c0,.32.16.48.49.48.35,0,.53-.17.53-.51v-1.36c0-.39-.08-.73-.23-1.02l-.91-1.75c-.19-.36-.28-.72-.28-1.08v-1.15c0-.55.3-.82.91-.82s.9.25.9.74v1.42h-.4v-1.37c0-.3-.16-.45-.47-.45s-.47.16-.47.47v1.23c0,.28.06.53.17.76l.89,1.72c.22.42.32.82.32,1.2v1.49c0,.54-.31.81-.93.81s-.92-.28-.92-.85v-1.41h.4Z"
              />
              <path fill="currentColor" d="M245.59,44.13l.68-4.3h.44l-.91,4.8v3.13h-.44v-3.13l-.91-4.8h.44l.68,4.3h.02Z" />
              <path
                fill="currentColor"
                d="M247.86,45.56v1.44c0,.32.16.48.49.48.35,0,.53-.17.53-.51v-1.36c0-.39-.08-.73-.23-1.02l-.91-1.75c-.19-.36-.28-.72-.28-1.08v-1.15c0-.55.3-.82.91-.82s.9.25.9.74v1.42h-.4v-1.37c0-.3-.16-.45-.47-.45s-.47.16-.47.47v1.23c0,.28.06.53.17.76l.89,1.72c.22.42.32.82.32,1.2v1.49c0,.54-.31.81-.93.81s-.92-.28-.92-.85v-1.41h.4Z"
              />
              <path fill="currentColor" d="M250.25,47.77v-.57h.48v.57h-.48Z" />
            </g>
            <g>
              <path
                fill="currentColor"
                d="M226.86,54.27c0-.38-.23-.48-.57-.48h-.48v3.81h-.72v-7.93h1.35c.69,0,1.12.12,1.12.91v2.01c0,.62-.23.82-.79.86v.02c.52.03.82.19.82.74v3.39h-.72v-3.33ZM226.29,53.17c.34,0,.54-.1.54-.57v-1.78c0-.46-.24-.54-.62-.54h-.41v2.89h.48Z"
              />
              <path fill="currentColor" d="M228.92,51.62h.64l.57,5.06h.02l.57-5.06h.64l-.71,5.99h-1.01l-.71-5.99Z" />
              <path
                fill="currentColor"
                d="M234.57,55.4h-1.27l-.3,2.2h-.72l1.16-7.93h.99l1.16,7.93h-.72l-.3-2.2ZM233.94,50.22h-.02l-.55,4.57h1.12l-.55-4.57Z"
              />
              <path
                fill="currentColor"
                d="M240.47,50.73c0-.34-.12-.51-.41-.51s-.41.16-.41.51v5.82c0,.34.12.51.41.51s.41-.16.41-.51v-1.11h.72v1.13c0,.67-.37,1.1-1.13,1.1s-1.13-.43-1.13-1.1v-5.87c0-.67.37-1.1,1.13-1.1s1.13.43,1.13,1.1v1.04h-.72v-1.02Z"
              />
              <path
                fill="currentColor"
                d="M244.5,49.67h2.22v.62h-1.54v2.68h.02c.14-.22.38-.36.66-.36.55,0,.91.31.91,1.02v2.84c0,.71-.33,1.2-1.14,1.2s-1.15-.48-1.15-1.2v-1.08h.68v1.08c0,.41.1.65.47.65s.46-.24.46-.65v-2.67c0-.41-.1-.65-.45-.65s-.46.24-.46.65v.32h-.68v-4.45Z"
              />
              <path
                fill="currentColor"
                d="M249.23,55.51h-1.41v-.8l1.1-5.04h1.03v5.29h.57v.55h-.57v2.09h-.72v-2.09ZM249.23,50.51h-.02l-.83,4.46h.86v-4.46Z"
              />
              <path
                fill="currentColor"
                d="M251.59,54.26c0-.52.18-.69.57-.76v-.02c-.4-.16-.57-.3-.57-.88v-1.8c0-.71.33-1.2,1.13-1.2s1.13.48,1.13,1.2v1.8c0,.58-.18.71-.57.88v.02c.4.07.57.24.57.76v2.21c0,.71-.33,1.2-1.13,1.2s-1.13-.48-1.13-1.2v-2.21ZM253.17,50.65c0-.26-.18-.49-.45-.49s-.45.23-.45.49v2.05c0,.26.18.49.45.49s.45-.23.45-.49v-2.05ZM253.17,54.24c0-.26-.18-.49-.45-.49s-.45.23-.45.49v2.38c0,.26.18.49.45.49s.45-.23.45-.49v-2.38Z"
              />
            </g>
          </g>
          <g>
            <path fill="currentColor" d="M72.48,13.05h2.63v11.97h-2.63v-11.97Z" />
            <path
              fill="currentColor"
              d="M76.86,23.28l1.56-1.86c1.08.89,2.21,1.45,3.58,1.45,1.08,0,1.73-.43,1.73-1.13v-.03c0-.67-.41-1.01-2.41-1.52-2.41-.62-3.97-1.28-3.97-3.66v-.03c0-2.17,1.75-3.61,4.19-3.61,1.75,0,3.23.55,4.45,1.52l-1.37,1.98c-1.06-.74-2.1-1.18-3.11-1.18s-1.54.46-1.54,1.04v.03c0,.79.51,1.04,2.58,1.57,2.43.63,3.8,1.5,3.8,3.59v.03c0,2.38-1.81,3.71-4.4,3.71-1.81,0-3.64-.63-5.08-1.92Z"
            />
            <path
              fill="currentColor"
              d="M87.67,19.07v-.03c0-3.4,2.69-6.19,6.38-6.19s6.35,2.75,6.35,6.16v.03c0,3.4-2.69,6.19-6.38,6.19s-6.35-2.75-6.35-6.16ZM97.64,19.07v-.03c0-2.05-1.5-3.76-3.63-3.76s-3.59,1.68-3.59,3.73v.03c0,2.05,1.51,3.76,3.63,3.76s3.59-1.68,3.59-3.73Z"
            />
            <path
              fill="currentColor"
              d="M106.41,22.94l3.99-3.27c1.49-1.23,2.07-1.88,2.07-2.87s-.67-1.56-1.61-1.56-1.56.51-2.45,1.61l-1.85-1.49c1.18-1.61,2.33-2.48,4.46-2.48,2.48,0,4.12,1.45,4.12,3.69v.03c0,2-1.03,2.99-3.15,4.64l-1.95,1.5h5.22v2.28h-8.86v-2.09Z"
            />
            <path fill="currentColor" d="M122.5,15.33h-5.63v-2.27h8.62v1.98l-5.37,9.99h-2.98l5.35-9.7Z" />
            <path
              fill="currentColor"
              d="M126.45,19.07v-.03c0-3.47,2.16-6.19,5.34-6.19s5.3,2.69,5.3,6.16v.03c0,3.47-2.14,6.19-5.34,6.19s-5.3-2.69-5.3-6.16ZM134.39,19.07v-.03c0-2.17-1.06-3.81-2.63-3.81s-2.6,1.59-2.6,3.78v.03c0,2.19,1.04,3.81,2.63,3.81s2.6-1.61,2.6-3.78Z"
            />
            <path
              fill="currentColor"
              d="M138.51,19.07v-.03c0-3.47,2.16-6.19,5.34-6.19s5.3,2.69,5.3,6.16v.03c0,3.47-2.14,6.19-5.34,6.19s-5.3-2.69-5.3-6.16ZM146.44,19.07v-.03c0-2.17-1.06-3.81-2.63-3.81s-2.6,1.59-2.6,3.78v.03c0,2.19,1.04,3.81,2.63,3.81s2.6-1.61,2.6-3.78Z"
            />
            <path fill="currentColor" d="M152.6,15.64l-2.09.51-.55-2.16,3.44-1.03h1.8v12.06h-2.6v-9.39Z" />
            <path fill="currentColor" d="M157.49,15.86h2.77v2.74h-2.77v-2.74ZM157.49,22.29h2.77v2.74h-2.77v-2.74Z" />
            <path
              fill="currentColor"
              d="M161.81,22.94l3.99-3.27c1.49-1.23,2.07-1.88,2.07-2.87s-.67-1.56-1.61-1.56-1.56.51-2.45,1.61l-1.85-1.49c1.18-1.61,2.33-2.48,4.46-2.48,2.48,0,4.12,1.45,4.12,3.69v.03c0,2-1.03,2.99-3.15,4.64l-1.95,1.5h5.22v2.28h-8.86v-2.09Z"
            />
            <path
              fill="currentColor"
              d="M172.12,19.07v-.03c0-3.47,2.16-6.19,5.34-6.19s5.3,2.69,5.3,6.16v.03c0,3.47-2.14,6.19-5.34,6.19s-5.3-2.69-5.3-6.16ZM180.06,19.07v-.03c0-2.17-1.06-3.81-2.63-3.81s-2.6,1.59-2.6,3.78v.03c0,2.19,1.04,3.81,2.63,3.81s2.6-1.61,2.6-3.78Z"
            />
            <path
              fill="currentColor"
              d="M183.92,22.94l3.99-3.27c1.49-1.23,2.07-1.88,2.07-2.87s-.67-1.56-1.61-1.56-1.56.51-2.45,1.61l-1.85-1.49c1.18-1.61,2.33-2.48,4.46-2.48,2.48,0,4.12,1.45,4.12,3.69v.03c0,2-1.03,2.99-3.15,4.64l-1.95,1.5h5.22v2.28h-8.86v-2.09Z"
            />
            <path
              fill="currentColor"
              d="M194.15,22.94l3.99-3.27c1.49-1.23,2.07-1.88,2.07-2.87s-.67-1.56-1.61-1.56-1.56.51-2.45,1.61l-1.85-1.49c1.18-1.61,2.33-2.48,4.46-2.48,2.48,0,4.12,1.45,4.12,3.69v.03c0,2-1.03,2.99-3.15,4.64l-1.95,1.5h5.22v2.28h-8.86v-2.09Z"
            />
          </g>
        </svg>
        <ul className="flex gap-4 items-center text-white text-md">
          <li>
            <LosseLink to="/cookieverklaring">Cookieverklaring</LosseLink>
          </li>
          <li>
            <LosseLink to="/privacyverklaring">Privacyverklaring</LosseLink>
          </li>
        </ul>
      </div>
    </footer>
  )
}

function FooterTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-lg md:text-2xl font-bold text-metric-electrike mb-1 md:mb-14">{children}</h2>
}
